import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Clock, HandHeart } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";

const TeamStatus = ({ t, onApply }) => {
  if (t.is_member) return <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: "var(--sj-secondary)" }}><Check className="h-3.5 w-3.5" /> You serve here</span>;
  if (t.application_status === "pending") return <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: "#084438" }}><Clock className="h-3.5 w-3.5" /> Application pending</span>;
  return (
    <button data-testid={`apply-${t.id}`} onClick={() => onApply(t)} className="text-xs font-semibold rounded-full px-4 py-1.5 text-white hover:brightness-105" style={{ background: "var(--sj-primary)" }}>
      {t.application_status === "rejected" ? "Apply again" : "Apply to join"}
    </button>
  );
};

export const JoinTeamsPanel = ({ onApplied }) => {
  const [teams, setTeams] = useState([]);
  const [applyFor, setApplyFor] = useState(null);
  const [reason, setReason] = useState("");
  const load = () => api.get("/portal/teams").then(({ data }) => setTeams(data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/portal/teams/${applyFor.id}/apply`, { reason });
      toast.success(`Application sent to join ${applyFor.name}!`);
      setApplyFor(null); setReason(""); load(); onApplied?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className={`${CARD} p-6 md:p-8`} data-testid="join-teams-panel">
      <div className="flex items-center gap-2 mb-1"><HandHeart className="h-5 w-5" style={{ color: "var(--sj-primary)" }} /><h3 className="font-head text-xl font-semibold">Join a team</h3></div>
      <p className="text-sm mb-5" style={{ color: "var(--sj-text-2)" }}>Apply to serve on a ministry — the team's leaders will review your application.</p>
      {teams.length === 0 && <p className="text-sm" style={{ color: "var(--sj-text-3)" }}>No teams are open for applications right now.</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {teams.map((t) => (
          <div key={t.id} data-testid={`serve-team-${t.id}`} className="rounded-xl border p-4 flex flex-col transition-shadow hover:shadow-md" style={{ borderColor: "var(--sj-border)" }}>
            <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full self-start" style={{ background: "var(--sj-peach)", color: "var(--sj-primary)" }}>{t.category}</span>
            <div className="mt-2 font-semibold">{t.name}</div>
            <p className="text-xs mt-0.5 line-clamp-2 flex-1" style={{ color: "var(--sj-text-2)" }}>{t.description}</p>
            <div className="mt-3"><TeamStatus t={t} onApply={setApplyFor} /></div>
          </div>
        ))}
      </div>
      <Dialog open={!!applyFor} onOpenChange={(o) => !o && setApplyFor(null)}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-head text-2xl">Apply to join {applyFor?.name}</DialogTitle>
            <DialogDescription>Your application goes to the team's leaders.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <label className="block text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>Why would you like to serve? (optional)
              <textarea data-testid="apply-reason-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Share a little about your heart to serve..."
                className="mt-1.5 w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/20 px-4 py-2.5 text-sm normal-case font-normal tracking-normal" />
            </label>
            <DialogFooter>
              <button type="button" onClick={() => setApplyFor(null)} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
              <button type="submit" data-testid="submit-apply-button" className="rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105" style={{ background: "var(--sj-primary)" }}>Send Application</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
